import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import {
  Award,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Gavel,
  MessageSquare,
  Scale,
  Shield,
} from "lucide-react";
import { useState } from "react";
import { editAppealCouncilLitigation } from "services/advocateComplaintService";
import { CouncilLitigation } from "types";
import { DateView } from "utils/helper";
import { ADVOCATE_ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";

interface CouncilLitigationProps {
  councilLitigationData: CouncilLitigation[];
  onCouncilLitigationRefresh: any;
}

const calculate_remaining_days = ({ date }: { date: string }) => {
  const appeal = new Date(date ?? "");
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);
  return remainingDays;
};

const getStatusColor = (days: number, isDarkMode = false) => {
  if (days < 0) {
    return isDarkMode
      ? "text-red-400 bg-red-900/20 border-red-800"
      : "text-red-600 bg-red-50 border-red-200";
  }
  if (days <= 7) {
    return isDarkMode
      ? "text-amber-400 bg-amber-900/20 border-amber-800"
      : "text-amber-600 bg-amber-50 border-amber-200";
  }
  return isDarkMode
    ? "text-green-400 bg-green-900/20 border-green-800"
    : "text-green-700 bg-green-50 border-green-200";
};

const getStatusText = (days: number, t: any) => {
  const absDays = Math.abs(days);
  const dayLabel = absDays === 1 ? t("day") : t("days");

  if (days < 0) return `${t("expired")} ${absDays} ${dayLabel} ${t("ago")}`;
  if (days === 0) return t("dueToday");
  if (days === 1) return t("dueTomorrow");
  return `${days} ${dayLabel} ${t("remaining")}`;
};

const getCouncilDecisionBadge = (hasDecision: boolean, t: any) => {
  if (hasDecision) {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 text-xs font-medium">
        <Award className="w-3 h-3" />
        {t("appealDecision")}
      </span>
    );
  }
  return null;
};

export default function AppealCouncilLitigationCard({
  councilLitigationData,
  onCouncilLitigationRefresh,
}: CouncilLitigationProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const canViewDailyHearing = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const [expandedHearing, setExpandedHearing] = useState<string | null>(null);
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState({
    hearing_date: "",
    next_appointment_date: "",
  });
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);

  const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startDateEdit = (obj: CouncilLitigation) => {
    setEditingDateId(String(obj?.id));
    setDateDraft({
      hearing_date: toDateInputValue(obj?.hearing_date),
      next_appointment_date: toDateInputValue(obj?.next_appointment_date),
    });
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setDateDraft({ hearing_date: "", next_appointment_date: "" });
  };

  const handleUpdateDate = async (obj: CouncilLitigation) => {
    if (!obj?.id) {
      showToast("Litigation record not found", "error");
      return;
    }

    const currentHearing = toDateInputValue(obj?.hearing_date);
    const currentNext = toDateInputValue(obj?.next_appointment_date);

    const payload: any = {};
    if (dateDraft.hearing_date && dateDraft.hearing_date !== currentHearing) {
      payload.hearing_date = dateDraft.hearing_date;
    }
    if (
      dateDraft.next_appointment_date &&
      dateDraft.next_appointment_date !== currentNext
    ) {
      payload.next_appointment_date = dateDraft.next_appointment_date;
    }

    if (!payload.hearing_date && !payload.next_appointment_date) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingDate(true);
    try {
      await editAppealCouncilLitigation(payload, String(obj.id), false);
      await Promise.resolve(onCouncilLitigationRefresh?.());
      cancelDateEdit();
      showToast("Date updated", "success");
    } catch (err) {
      showToast("Failed to update date", "error");
    } finally {
      setIsUpdatingDate(false);
    }
  };

  const hasCouncilDecision = councilLitigationData[0]?.council_decision_day;

  // Sort and filter data
  const sortedData = [...(councilLitigationData || [])]
    .sort((a, b) => {
      const aHasDecision = !!a.council_decision_summary;
      const bHasDecision = !!b.council_decision_summary;

      // If only one has a decision, show it first
      if (aHasDecision && !bHasDecision) return -1;
      if (!aHasDecision && bHasDecision) return 1;

      // Otherwise, sort by next appointment date ascending
      const dateA = new Date(a?.next_appointment_date ?? "").getTime();
      const dateB = new Date(b?.next_appointment_date ?? "").getTime();

      return dateA - dateB;
    })
    .filter((obj) => !obj.council_decision_day);

  const toggleHearingExpansion = (id: number | string | undefined) => {
    if (id === undefined || id === null) return;
    const idStr = String(id);
    setExpandedHearing((prev) => (prev === idStr ? null : idStr));
  };

  return (
    <div className="space-y-6">
      {/* Council Decision Card - Prominent Display */}
      {hasCouncilDecision && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-dark-red-600 to-dark-red-700 h-2 border border-dark-red-500"></div>
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-xl">
                  <Shield className="w-7 h-7 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {t("appealCouncilDecision")}
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t("finalDecisionRendered")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getCouncilDecisionBadge(true, t)}
                {councilLitigationData[0]?.council_decision_day && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                    <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {DateView(councilLitigationData[0].council_decision_day)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Key Decision Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t("decisionDate")}
                  </p>
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {councilLitigationData[0]?.council_decision_day
                    ? DateView(councilLitigationData[0].council_decision_day)
                    : "-"}
                </p>
              </div>

              {councilLitigationData[0]?.ammended_council_decision_type && (
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {t("decisionType")}
                    </p>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {councilLitigationData[0]?.ammended_council_decision_type ||
                      "-"}
                  </p>
                </div>
              )}

              {councilLitigationData[0]?.ammended_discipline_measure_type && (
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Gavel className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {t("measureType")}
                    </p>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {councilLitigationData[0]
                      ?.ammended_discipline_measure_type || "-"}
                  </p>
                </div>
              )}
            </div>

            {/* Appeal Deadline (if applicable) */}
            {councilLitigationData[0]?.council_decision_day && (
              <div
                className={`p-4 rounded-lg border mb-6 ${getStatusColor(
                  calculate_remaining_days({
                    date: councilLitigationData[0]?.council_decision_day ?? "",
                  }),
                  false,
                )}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5" />
                    <div>
                      <p className="font-medium">{t("appealDeadlineStatus")}</p>
                      <p className="text-sm opacity-80">
                        {t("deadline")}:{" "}
                        {councilLitigationData[0]?.council_decision_day
                          ? DateView(
                              councilLitigationData[0].council_decision_day,
                            )
                          : "-"}
                      </p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold">
                    {getStatusText(
                      calculate_remaining_days({
                        date:
                          councilLitigationData[0]?.council_decision_day ?? "",
                      }),
                      t,
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Summary Sections */}
            <div className="space-y-6">
              {councilLitigationData[0]?.council_decision_summary && (
                <div className="bg-white dark:bg-gray-700/30 p-5 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t("councilDecisionSummary")}
                    </h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {councilLitigationData[0]?.council_decision_summary || "-"}
                  </p>
                </div>
              )}

              {councilLitigationData[0]?.advocate_claim_summary && (
                <div className="bg-white dark:bg-gray-700/30 p-5 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t("advocateClaimSummary")}
                    </h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {councilLitigationData[0]?.advocate_claim_summary || "-"}
                  </p>
                </div>
              )}
            </div>

            {/* Files Section */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <Download className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t("decisionDocuments")}
                </h3>
              </div>
              <DocumentViewerModal
                documents={councilLitigationData[0]?.verdict_documents || []}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                layout="list"
                hideEmptyFile={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Daily Hearings */}
      {sortedData.length > 0 && (
        <div className="space-y-6">
          <div className="space-y-6">
            {sortedData.map((obj) =>
              canViewDailyHearing || obj?.council_decision_summary ? (
                <div
                  key={obj.id}
                  className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border hover:shadow-md transition-all duration-200 ${
                    obj?.council_decision_summary
                      ? "border-l-4 border-red-500 dark:border-red-400"
                      : "border-l-4 border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {/* Hearing Header - Collapsible */}
                  <div
                    className="p-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-t-xl transition-colors"
                    onClick={() => toggleHearingExpansion(obj.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-3 rounded-lg ${
                            obj?.council_decision_summary
                              ? "bg-red-50 dark:bg-red-900/20"
                              : "bg-gray-100 dark:bg-gray-700"
                          }`}
                        >
                          <Gavel
                            className={`w-5 h-5 ${
                              obj?.council_decision_summary
                                ? "text-red-600 dark:text-red-400"
                                : "text-gray-600 dark:text-gray-400"
                            }`}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                              {t("appealHearingSession")}
                            </h3>
                            {obj?.council_decision_summary && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium">
                                <CheckCircle className="w-3 h-3" />
                                {t("decisionMade")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {obj?.next_appointment_date && (
                          <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-red-50 to-indigo-50 dark:from-red-900/20 dark:to-indigo-900/20 rounded-full border border-red-200 dark:border-red-800">
                            <Clock className="w-4 h-4 text-red-600 dark:text-red-400" />
                            <div className="text-xs">
                              <div className="font-semibold text-red-700 dark:text-red-300">
                                {t("nextHearing")}
                              </div>
                              <div className="text-red-600 dark:text-red-400">
                                {DateView(obj.next_appointment_date)}
                              </div>
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            startDateEdit(obj);
                          }}
                          className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                        >
                          {t("updateDate")}
                        </button>
                        <ChevronRight
                          className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${
                            expandedHearing === String(obj.id)
                              ? "rotate-90"
                              : ""
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  {expandedHearing === String(obj.id) && (
                    <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-5 space-y-5">
                      {editingDateId === String(obj?.id) && (
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t("hearingDate")}
                              </label>
                              <input
                                type="date"
                                value={dateDraft.hearing_date}
                                onChange={(e) =>
                                  setDateDraft((prev) => ({
                                    ...prev,
                                    hearing_date: e.target.value,
                                  }))
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t("nextAppointment")}
                              </label>
                              <input
                                type="date"
                                value={dateDraft.next_appointment_date}
                                onChange={(e) =>
                                  setDateDraft((prev) => ({
                                    ...prev,
                                    next_appointment_date: e.target.value,
                                  }))
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={isUpdatingDate}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateDate(obj);
                              }}
                              className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                            >
                              {isUpdatingDate ? "Updating..." : "Save Date"}
                            </button>
                            <button
                              type="button"
                              disabled={isUpdatingDate}
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelDateEdit();
                              }}
                              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                            >
                              {t("cancel")}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Council Order (if not shown in header) */}
                      {obj?.given_order && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <Scale className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            <h4 className="font-medium text-gray-700 dark:text-gray-300">
                              {t("councilOrder")}
                            </h4>
                          </div>
                          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
                            {obj.given_order}
                          </p>
                        </div>
                      )}

                      {/* Decision Information (when decision is provided) */}
                      {obj?.council_decision_summary && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {obj?.council_decision_type && (
                            <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2 mb-2">
                                <Scale className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {t("decisionType")}
                                </h4>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {obj.council_decision_type}
                              </p>
                            </div>
                          )}

                          {obj?.discipline_measure_type && (
                            <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2 mb-2">
                                <Gavel className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {t("measureType")}
                                </h4>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {obj.discipline_measure_type}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Summary Sections */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {obj?.daily_report_summary && (
                          <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t("dailyReportSummary")}
                              </h4>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                              {obj.daily_report_summary}
                            </p>
                          </div>
                        )}

                        {obj?.appointment_reason && (
                          <div className="bg-white dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t("appointmentReason")}
                              </h4>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                              {obj.appointment_reason}
                            </p>
                          </div>
                        )}

                        {obj?.council_decision_summary && (
                          <div className="md:col-span-2 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-100 dark:border-red-800">
                            <div className="flex items-center gap-2 mb-2">
                              <Award className="w-4 h-4 text-red-600 dark:text-red-400" />
                              <h4 className="text-sm font-medium text-red-700 dark:text-red-300">
                                {t("hearingDecisionSummary")}
                              </h4>
                            </div>
                            <p className="text-red-800 dark:text-red-200 text-sm leading-relaxed">
                              {obj.council_decision_summary}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Files Section */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <Download className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          </div>
                          <h4 className="font-medium text-gray-700 dark:text-gray-300">
                            {t("hearingDocuments")}
                          </h4>
                        </div>

                        <DocumentViewerModal
                          documents={obj?.verdict_documents || []}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}/advocate_dicipline_appeal/appeal-litigation-verdict-counciloffice-advocate-documents`}
                          layout="list"
                          hideEmptyFile={true}
                        />

                        {/* Special Files */}
                        <div className="space-y-3">
                          {obj?.council_decision && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-200 dark:bg-gray-600 rounded">
                                  <FileText className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t("councilDecisionFile")}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t("officialDecisionDocument")}
                                  </p>
                                </div>
                              </div>
                              <FileActions fileUrl={obj.council_decision} />
                            </div>
                          )}

                          {obj?.termination_document && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-200 dark:bg-gray-600 rounded">
                                  <FileText className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {t("terminationFile")}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t("caseTerminationDocument")}
                                  </p>
                                </div>
                              </div>
                              <FileActions fileUrl={obj.termination_document} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null,
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!hasCouncilDecision && sortedData.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <FileText className="w-10 h-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t("noAppealLitigationData")}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
            {t("noAppealLitigationMessage")}
          </p>
        </div>
      )}
    </div>
  );
}
